import { Button, Flex, Tooltip } from "antd"
import { PictureOutlined } from "@ant-design/icons"
import classNames from "classnames"
import type { PreviewFrameSize } from "~/components/ResourcePreview/previewFrame"
import { VideoResourceCard } from "./VideoResourceCard"
import styles from "./index.css"
import { ResourcePreview } from "~/components/ResourcePreview"
import { SortableResourceList } from "~/components/SortableResourceList"
import {
  getUploadedResourceKind,
  type UploadedResource,
} from "~/common/media/uploadedResources"

type PreviewResource = UploadedResource & { label?: string }

type ReferencePreviewListProps = {
  references: PreviewResource[]
  sortable: boolean
  wrap?: boolean
  size?: PreviewFrameSize
  videoDisplay?: "thumbnail" | "details" | "metadata"
  onWhiteBgImage?: (url: string) => void
  videoPreviewRange?: { start: number; end: number }
  onRemove: (url: string) => void
  onReorder: (references: UploadedResource[]) => void
}

const getResourceId = (resource: UploadedResource) => resource.url

const isImageResource = (resource: UploadedResource) => {
  return getUploadedResourceKind(resource) === "image"
}

const getImageDragAriaLabel = (
  _resource: UploadedResource,
  _index: number,
  sortableIndex: number,
) => {
  return `拖动调整图片 ${sortableIndex + 1} 顺序`
}

export const ReferencePreviewList = ({
  references,
  sortable,
  wrap = true,
  size = "small",
  videoDisplay = "thumbnail",
  onWhiteBgImage,
  videoPreviewRange,
  onRemove,
  onReorder,
}: ReferencePreviewListProps) => {
  return (
    <SortableResourceList<PreviewResource>
      className={classNames(styles.list, !wrap && styles.singleRow)}
      gap={size === "small" ? 8 : 12}
      wrap={wrap}
      getItemClassName={(resource) =>
        classNames(
          styles.item,
          getUploadedResourceKind(resource) === "video" &&
            videoDisplay !== "thumbnail" &&
            styles.videoResourceItem,
        )
      }
      getItemAriaLabel={getImageDragAriaLabel}
      getItemId={getResourceId}
      isItemSortable={isImageResource}
      items={references}
      renderItem={(resource, _index, { sortableIndex, isSortingEnabled }) => {
        const kind = getUploadedResourceKind(resource)

        if (kind === "image") {
          const imageNumber = (sortableIndex ?? 0) + 1

          return (
            <>
              {onWhiteBgImage ? (
                <Tooltip title="AI生成白底图">
                  <Button
                    aria-label="AI生成白底图"
                    className={styles.whiteBgButton}
                    icon={<PictureOutlined />}
                    onClick={() => onWhiteBgImage(resource.url)}
                    shape="circle"
                    size="small"
                  />
                </Tooltip>
              ) : null}
              <ResourcePreview
                type="image"
                size={size}
                preview={{ mask: "预览" }}
                src={resource.url}
                label={resource.label}
                alt={resource.name || `参考图片 ${imageNumber}`}
                onRemove={() => onRemove(resource.url)}
              />
              {isSortingEnabled && size !== "small" ? (
                <Flex
                  align="center"
                  justify="center"
                  aria-hidden
                  className={styles.resourceOrder}
                >
                  {imageNumber}
                </Flex>
              ) : null}
            </>
          )
        }

        if (kind === "video") {
          if (videoDisplay !== "thumbnail") {
            return (
              <VideoResourceCard
                resource={resource}
                showVideoPreview={videoDisplay === "details"}
                videoPreviewRange={videoPreviewRange}
                onRemove={() => onRemove(resource.url)}
              />
            )
          }

          return (
            <ResourcePreview
              type="video"
              size={size}
              src={resource.url}
              startTime={videoPreviewRange?.start}
              endTime={videoPreviewRange?.end}
              onRemove={() => onRemove(resource.url)}
            />
          )
        }

        if (kind === "file") {
          return (
            <ResourcePreview
              type="file"
              size={size}
              label={resource.name || "文件"}
              onRemove={() => onRemove(resource.url)}
            />
          )
        }

        return (
          <ResourcePreview
            type="audio"
            fingerprint={resource.id || resource.url}
            label={
              resource.label ??
              (size === "small" ? resource.name || "音频参考" : undefined)
            }
            size={size}
            src={resource.url}
            onRemove={() => onRemove(resource.url)}
          />
        )
      }}
      sortable={sortable}
      onReorder={onReorder}
    />
  )
}
